import React, { useMemo } from 'react';
import { Shuffle, TrendingUp } from 'lucide-react';
import type { Player } from '../../types';
import { BaseballLineupGrid } from './BaseballLineupGrid';

interface DepthChartEntry {
  id?: string;
  player_id: string | null;
  position: string;
  depth: number;
  unit?: string;
  color?: string;
}

interface BaseballLineupViewProps {
  players: Player[];
  depthChart: DepthChartEntry[];
  activeTab: string;
  onPlayerChange: (battingOrder: number, playerId: string | null) => void;
  playerSortBy: 'firstName' | 'lastName' | 'number';
  onRandomizeClick?: () => void;
  onOptimizeClick?: () => void;
  playerStatsMap?: Map<string, { batting_average?: number; on_base_percentage?: number; batting_hand?: 'L' | 'R' | 'S' }>;
}

export const BaseballLineupView: React.FC<BaseballLineupViewProps> = ({
  players,
  depthChart,
  activeTab,
  onPlayerChange,
  onRandomizeClick,
  onOptimizeClick,
  playerStatsMap
}) => {
  const getPlayerForBattingOrder = (battingOrder: number): Player | null => {
    const entry = depthChart.find(
      e => e.position === battingOrder.toString() && e.unit === activeTab && e.depth === 1
    );
    if (!entry?.player_id) return null;
    return players.find(p => p.id === entry.player_id) || null;
  };

  const slotCount = Math.max(9, players.length);
  const lineup = useMemo(() => {
    return Array.from({ length: slotCount }, (_, i) => {
      const order = i + 1;
      return { order, player: getPlayerForBattingOrder(order) };
    });
  }, [depthChart, activeTab, players, slotCount]);

  return (
    <div className="space-y-4">
      <div className="mb-2 flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold mb-1">Batting Order</h3>
          <p className="text-xs text-gray-600">Drag players to set the batting order.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onRandomizeClick}
            className="btn-secondary flex items-center gap-1.5 px-3 py-1.5 text-xs"
            title="Randomize batting order"
          >
            <Shuffle className="w-3 h-3" />
            Randomize
          </button>
          <button
            onClick={onOptimizeClick}
            className="btn-secondary flex items-center gap-1.5 px-3 py-1.5 text-xs"
            title="Optimize batting order based on OBP and AVG"
          >
            <TrendingUp className="w-3 h-3" />
            Optimize
          </button>
        </div>
      </div>

      <BaseballLineupGrid
        lineup={lineup}
        allPlayers={players}
        onPlayerChange={onPlayerChange}
        playerStatsMap={playerStatsMap}
        slotCount={slotCount}
      />
    </div>
  );
};
